import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import css from './Carousel.module.css';

const KEY_CODE_ARROW_LEFT = 37;
const KEY_CODE_ARROW_RIGHT = 39;

/**
 * Reusable Carousel component that handles scrolling and navigation
 * 
 * @component
 * @param {Object} props
 * @param {React.ReactNode} props.children - The items to render in the carousel
 * @param {string} props.id - Unique identifier for the carousel
 * @param {number} props.numColumns - Number of visible columns (affects scrolling behavior)
 * @param {string} props.className - Additional CSS classes for the container
 * @param {string} props.itemClassName - CSS classes for individual items
 * @param {boolean} props.showArrows - Whether to show navigation arrows (default: true)
 * @param {string} props.arrowPrevClassName - Custom class for previous arrow
 * @param {string} props.arrowNextClassName - Custom class for next arrow
 * @returns {JSX.Element} Carousel component
 */
const Carousel = ({
  children,
  id,
  numColumns = 1,
  className,
  itemClassName,
  showArrows = true,
  arrowPrevClassName,
  arrowNextClassName,
}) => {
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  
  const sliderContainerId = `${id}-container`;
  const sliderId = `${id}-slider`;
  const childrenArray = React.Children.toArray(children);
  const numberOfItems = childrenArray.length;
  const hasItems = numberOfItems > 0;

  // Function to check scroll position and update arrows
  const updateArrowVisibility = (slider) => {
    if (!slider) return;
    
    // Check if we can scroll left (not at start)
    setShowLeftArrow(slider.scrollLeft > 0);
    
    // Check if we can scroll right (not at end)
    const canScrollRight = slider.scrollLeft < (slider.scrollWidth - slider.clientWidth - 2); // -2 for rounding errors
    setShowRightArrow(canScrollRight);
  };

  useEffect(() => {
    const setCarouselWidth = () => {
      if (hasItems) {
        const windowWidth = window.innerWidth;
        const elem = window.document.getElementById(sliderContainerId);
        if (!elem) return;
        
        const scrollbarWidth = window.innerWidth - document.body.clientWidth;
        const elementWidth =
          elem.clientWidth >= windowWidth - scrollbarWidth ? windowWidth : elem.clientWidth;
        const carouselWidth = elementWidth - scrollbarWidth;

        elem.style.setProperty('--carouselWidth', `${carouselWidth}px`);
        
        // Initialize arrow visibility after setting width
        const slider = window.document.getElementById(sliderId);
        if (slider) {
          updateArrowVisibility(slider);
        }
      }
    };
    setCarouselWidth();

    window.addEventListener('resize', setCarouselWidth);
    return () => window.removeEventListener('resize', setCarouselWidth);
  }, [hasItems, sliderContainerId, sliderId]);

  // Add scroll event listener to update arrows during manual scrolling
  useEffect(() => {
    if (hasItems) {
      const slider = document.getElementById(sliderId);
      if (slider) {
        const handleScroll = () => updateArrowVisibility(slider);
        slider.addEventListener('scroll', handleScroll);
        return () => slider.removeEventListener('scroll', handleScroll);
      }
    }
  }, [hasItems, sliderId]);

  const onSlideLeft = (e) => {
    const slider = window.document.getElementById(sliderId);
    if (!slider) return;
    
    const slideWidth = numColumns * slider?.firstChild?.clientWidth;
    slider.scrollLeft = slider.scrollLeft - slideWidth;
    // Update arrow visibility after scrolling
    setTimeout(() => updateArrowVisibility(slider), 100);
    // Fix for Safari
    e.target.focus();
  };

  const onSlideRight = (e) => {
    const slider = window.document.getElementById(sliderId);
    if (!slider) return;
    
    const slideWidth = numColumns * slider?.firstChild?.clientWidth;
    slider.scrollLeft = slider.scrollLeft + slideWidth;
    // Update arrow visibility after scrolling
    setTimeout(() => updateArrowVisibility(slider), 100);
    // Fix for Safari
    e.target.focus();
  };

  const onKeyDown = (e) => {
    if (e.keyCode === KEY_CODE_ARROW_LEFT) {
      // Prevent changing cursor position in input
      e.preventDefault();
      onSlideLeft(e);
    } else if (e.keyCode === KEY_CODE_ARROW_RIGHT) {
      // Prevent changing cursor position in input
      e.preventDefault();
      onSlideRight(e);
    }
  };

  if (!hasItems) {
    return null;
  }

  return (
    <div className={classNames(css.carouselContainer, className)} id={sliderContainerId}>
      {showArrows && (
        <div
          className={classNames(css.carouselArrows, {
            [css.notEnoughBlocks]: numberOfItems <= numColumns,
          })}
        >
          {showLeftArrow && (
            <button 
              className={classNames(css.carouselArrowPrev, arrowPrevClassName)} 
              onClick={onSlideLeft} 
              onKeyDown={onKeyDown}
              aria-label="Previous items"
            >
              ‹
            </button>
          )}
          {showRightArrow && (
            <button 
              className={classNames(css.carouselArrowNext, arrowNextClassName)} 
              onClick={onSlideRight} 
              onKeyDown={onKeyDown}
              aria-label="Next items"
            >
              ›
            </button>
          )}
        </div>
      )}
      <div className={classNames(css.carouselSlider, 'py-3')} id={sliderId}>
        {childrenArray.map((child, index) => (
          <div key={index} className={classNames(css.carouselItem, itemClassName)}>
            {child}
          </div>
        ))}
      </div>
    </div>
  );
};

export default Carousel; 